import { useState, useEffect, useCallback } from 'react';
import { AttendanceService } from '../services/AttendanceService';
import { attendanceRepository } from '../repositories';
import type { CurrentUser, DailyAttendanceRecord, AttendanceStats } from '../models';

/**
 * 출결 데이터 관리 훅
 * 데이터 조회, 상태 관리, 에러 처리를 담당
 */
export const useAttendanceData = () => {
  const [service] = useState(() => new AttendanceService(attendanceRepository));
  const [currentUsers, setCurrentUsers] = useState<CurrentUser[]>([]);
  const [dailyRecords, setDailyRecords] = useState<DailyAttendanceRecord[]>([]);
  const [stats, setStats] = useState<AttendanceStats>({
    total_users: 0,
    current_users: 0,
    today_checkins: 0,
    average_session_minutes: 0,
    peak_hours: [],
    membership_breakdown: {},
    last_updated: '',
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 현재 이용자 목록 조회
   */
  const fetchCurrentUsers = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const users = await service.getCurrentUsers();
      setCurrentUsers(users);
    } catch (err) {
      setError('현재 이용자 데이터를 가져오는데 실패했습니다.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [service]);

  /**
   * 일일 출결 기록 조회
   */
  const fetchDailyRecords = useCallback(
    async (studyroomId: number, date: string): Promise<void> => {
      try {
        setLoading(true);
        setError(null);
        const records = await service.getDailyAttendance(studyroomId, date);
        setDailyRecords(records);
      } catch (err) {
        setError('일일 출결 기록을 가져오는데 실패했습니다.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [service]
  );

  /**
   * 출결 통계 조회
   */
  const fetchStats = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const statsData = await service.getStats();
      setStats(statsData);
    } catch (err) {
      setError('출결 통계를 가져오는데 실패했습니다.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [service]);

  return {
    currentUsers,
    dailyRecords,
    stats,
    loading,
    error,
    fetchCurrentUsers,
    fetchDailyRecords,
    fetchStats,
  };
};
